/**
 * Lucky buy box — behaviour for blocks/lucky-buy-box.liquid.
 *
 * - Custom <details> variant dropdown: selecting an option updates the
 *   summary, the "Style:" label, the hidden variant input, the ?variant=
 *   URL param, and closes the list. Outside clicks / Escape close it.
 * - The price row also FOLLOWS an external variant picker (the Lucky
 *   variant picker block used with the kit builder): on any picker change
 *   the live price and the Save pill recompute from the embedded
 *   [data-lbb-variants] JSON — needed on products whose variants are
 *   priced differently (600ml vs 1L bottle).
 * - Add to cart posts /cart/add.js and dispatches the theme's
 *   CartLinesUpdateEvent (same pattern as sauna-kit-builder) so the cart
 *   drawer opens and the header count updates. The bundle line adds qty 3 —
 *   the store's Buy 2 Get 1 Free automatic discount does the pricing.
 * - The Size & Fit link (when no URL is set) opens the Product Details
 *   accordion further down the column.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Format `cents` like `sample` (e.g. "€30,95", "$1,234.00"), keeping its
 * currency symbol and decimal/thousands separators.
 * @param {string} sample
 * @param {number} cents
 * @param {boolean} [trimZeros] drop ",00"/".00" on whole amounts
 */
const formatLike = (sample, cents, trimZeros = false) => {
  const commaDecimal = /\d,\d{2}(?!\d)/.test(sample);
  let [int, dec] = (Math.round(cents) / 100).toFixed(2).split('.');
  int = int.replace(/\B(?=(\d{3})+(?!\d))/g, commaDecimal ? '.' : ',');
  const amount = trimZeros && dec === '00' ? int : int + (commaDecimal ? ',' : '.') + dec;
  return sample.replace(/\d(?:[\d.,]*\d)?/, amount);
};

class LuckyBuyBox extends HTMLElement {
  /** @type {AbortController} */
  #abort = new AbortController();
  #busy = false;
  /** @type {Array<{id: string, price: string, cents: number}>} */
  #variants = [];

  connectedCallback() {
    if (this.__wired) return;
    this.__wired = true;
    const { signal } = this.#abort;

    try {
      this.#variants = JSON.parse(this.querySelector('[data-lbb-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    // Outside the shop currency the RRP row is rendered empty; fill it in with
    // the converted amount straight away.
    if (this.hasAttribute('data-rrp-convert')) this.#applyPrice(this.dataset.variantId);

    this.addEventListener('click', this.#onClick, { signal });

    // Follow the external variant picker so the price row stays truthful on
    // products with differently-priced variants.
    document.addEventListener('change', this.#onExternalPickerChange, { signal, capture: true });

    // Close the dropdown on outside click or Escape.
    document.addEventListener(
      'click',
      (e) => {
        const select = this.querySelector('[data-lbb-select]');
        if (select?.open && !select.contains(e.target)) select.open = false;
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return;
        const select = this.querySelector('[data-lbb-select]');
        if (select?.open) select.open = false;
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  #onClick = (event) => {
    const option = event.target.closest('[data-lbb-option]');
    if (option) {
      this.#selectVariant(option);
      return;
    }

    const sizefit = event.target.closest('[data-lbb-sizefit-fallback]');
    if (sizefit) {
      event.preventDefault();
      this.#openDetailsAccordion();
      return;
    }

    if (event.target.closest('[data-lbb-add-bundle]')) {
      this.#add(3, event.target.closest('[data-lbb-add-bundle]'));
      return;
    }

    if (event.target.closest('[data-lbb-add]')) {
      this.#add(1, this.querySelector('[data-lbb-add]'));
    }
  };

  /** A change inside any external variant-picker → resync the price row. */
  #onExternalPickerChange = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('variant-picker')) return;
    setTimeout(() => {
      const checked = document.querySelector('variant-picker input[type="radio"][data-variant-id]:checked');
      const id = checked?.dataset.variantId || new URL(window.location.href).searchParams.get('variant');
      if (id) this.#applyPrice(String(id));
    }, 0);
  };

  /**
   * Updates the live price, ATC price and Save pill for a variant id, from
   * the embedded variants JSON. RRP comes per-variant from the JSON
   * (rrpCents, falling back to the block-level data-rrp-cents); the
   * pill hides when the RRP no longer beats the price.
   * @param {string} id
   */
  #applyPrice(id) {
    const variant = this.#variants.find((v) => String(v.id) === String(id));
    if (!variant) return;

    const priceEl = this.querySelector('[data-lbb-price]');
    if (priceEl) priceEl.textContent = variant.price;
    const atcPrice = this.querySelector('[data-lbb-atc-price]');
    if (atcPrice) atcPrice.textContent = variant.price;

    let rrpCents = variant.rrpCents ?? parseInt(this.dataset.rrpCents || '0', 10);
    if (this.hasAttribute('data-rrp-convert')) {
      // RRP settings are in the shop currency; convert with the market's rate.
      const rate = parseFloat(window.Shopify?.currency?.rate);
      rrpCents = rate > 0 ? Math.round(rrpCents * rate) : 0;
    }
    const save = rrpCents - variant.cents;
    const rrpEl = this.querySelector('.lbb__rrp');
    const saveEl = this.querySelector('.lbb__pricerow .lbb__pill');
    if (rrpEl) {
      rrpEl.hidden = save <= 0;
      if (save > 0) {
        // Reuse the variant price's own formatting for the strikethrough.
        rrpEl.textContent = formatLike(variant.price, rrpCents);
      }
    }
    if (saveEl) {
      if (save > 0) {
        // Reuse the variant price's own formatting so the currency symbol
        // and separators stay market-correct.
        saveEl.textContent = 'Save ' + formatLike(variant.price, save, true);
        saveEl.hidden = false;
      } else {
        saveEl.hidden = true;
      }
    }
  }

  /** @param {HTMLButtonElement} option */
  #selectVariant(option) {
    const { variantId, variantTitle, variantPrice } = option.dataset;

    const input = this.querySelector('[data-lbb-variant-id]');
    if (input) input.value = variantId;

    const summaryValue = this.querySelector('[data-lbb-selected]');
    if (summaryValue) summaryValue.textContent = variantTitle;

    const styleLabel = this.querySelector('[data-lbb-style]');
    if (styleLabel) styleLabel.textContent = variantTitle;

    const price = this.querySelector('[data-lbb-price]');
    if (price) price.textContent = variantPrice;
    const atcPrice = this.querySelector('[data-lbb-atc-price]');
    if (atcPrice) atcPrice.textContent = variantPrice;
    this.#applyPrice(variantId);

    this.querySelectorAll('[data-lbb-option]').forEach((btn) => {
      btn.classList.toggle('lbb__option--selected', btn === option);
    });

    const select = this.querySelector('[data-lbb-select]');
    if (select) select.open = false;

    // Keep the URL shareable and let other components (sticky buy bar)
    // pick the selection up.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    history.replaceState({}, '', url);
    this.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #openDetailsAccordion() {
    const accordion = document.querySelector('.product-information accordion-custom, accordion-custom');
    const details = accordion?.querySelector('details');
    if (!details) return;
    details.open = true;
    accordion.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Mirrors sauna-kit-builder's add flow: one /cart/add.js call with a
   * CartLinesUpdateEvent whose promise resolves with fresh cart + section
   * HTML, which is what opens/refreshes the cart drawer.
   *
   * @param {number} quantity
   * @param {HTMLButtonElement} btn
   */
  async #add(quantity, btn) {
    if (this.#busy) return;
    const variantId = this.querySelector('[data-lbb-variant-id]')?.value;
    if (!variantId || !btn) return;

    this.#busy = true;
    const atc = this.querySelector('[data-lbb-add]');
    const textEl = this.querySelector('[data-lbb-atc-text]');
    const origHtml = textEl?.innerHTML;
    if (atc) atc.disabled = true;
    if (textEl) textEl.textContent = 'Adding…';

    const items = [{ id: Number(variantId), quantity }];

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    const target = document.querySelector('product-form-component') || document;
    target.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
        promise: deferred.promise,
      })
    );

    const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
    const restore = (message, delay) => {
      setTimeout(() => {
        if (textEl && origHtml) textEl.innerHTML = origHtml;
        if (atc) atc.disabled = false;
        this.#busy = false;
      }, delay);
      if (message && textEl) textEl.textContent = message;
    };

    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const response = await res.json();

      if (response.status) {
        target.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        const ajaxCart = await fetch('/cart.js').then((r) => r.json());
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, source: 'lucky-buy-box', sourceId: this.id, itemCount: quantity },
        });
        restore(response.message || 'Unavailable', 2500);
        return;
      }

      const cart = await fetch('/cart.js').then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'lucky-buy-box',
          sourceId: this.id,
          itemCount: quantity,
          sections: response.sections,
          didError: false,
        },
      });
      restore('✓ Added!', 2000);
    } catch (err) {
      deferred.reject(err);
      restore('Error — try again', 2500);
    }
  }
}

if (!customElements.get('lucky-buy-box')) {
  customElements.define('lucky-buy-box', LuckyBuyBox);
}
